<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Ajouter une visite virtuelle à votre site WordPress est plus simple
            qu'il n'y paraît. Que vous soyez agent immobilier, propriétaire
            d'hôtel ou chef d'entreprise, intégrer un tour interactif à 360°
            prend quelques minutes et ne demande aucune compétence en
            développement. Voici le guide complet étape par étape.
          </p>
        </section>

        <section>
          <h2>Méthode 1 — Intégration via iFrame (recommandée)</h2>
          <p>
            Cette méthode fonctionne sur tout site WordPress sans aucun plugin.
            Lorsque vous publiez une visite dans Travvir, vous obtenez un code
            d'intégration — un extrait HTML iFrame standard. Pour l'ajouter à
            votre page WordPress :
          </p>
          <ol>
            <li>
              Ouvrez l'éditeur WordPress (Gutenberg) pour la page souhaitée.
            </li>
            <li>Ajoutez un bloc "HTML personnalisé".</li>
            <li>Collez le code iFrame Travvir dans le bloc.</li>
            <li>Prévisualisez la page pour vérifier l'affichage.</li>
            <li>Publiez.</li>
          </ol>
          <p>
            L'intégration iFrame est compatible avec l'éditeur classique
            WordPress, Gutenberg, Elementor, Divi et tous les principaux
            constructeurs de pages — partout où vous pouvez ajouter un bloc ou
            widget HTML personnalisé.
          </p>
        </section>

        <section>
          <h2>Méthode 2 — Plugin WordPress de visite virtuelle</h2>
          <p>
            Plusieurs plugins WordPress supportent l'intégration de visites
            virtuelles 360°. Les options populaires incluent WP VR (pour
            héberger des images équirectangulaires localement) et Virtual Tour
            Easy (gestionnaire d'iFrames). Ces plugins ajoutent une interface
            conviviale pour gérer plusieurs visites intégrées sur votre site.
          </p>
          <h3>Quand utiliser un plugin</h3>
          <p>
            Utilisez un plugin si vous avez de nombreuses visites sur plusieurs
            pages et souhaitez une gestion centralisée, si vous voulez héberger
            les images de visite localement sur votre serveur, ou si vous avez
            besoin de gérer les permissions d'affichage par rôle utilisateur.
          </p>
        </section>

        <section>
          <h2>Méthode 3 — Shortcode</h2>
          <p>
            Si votre thème WordPress supporte les shortcodes dans les zones de
            contenu, vous pouvez créer un shortcode wrapper : ajoutez le code
            iFrame via <code>functions.php</code> ou un plugin de snippets.
            Utile pour les sites immobiliers où les tours apparaissent dans des
            modèles de fiches automatisées.
          </p>
        </section>

        <section>
          <h2>Bonnes pratiques pour les visites virtuelles WordPress</h2>
          <ul>
            <li>
              Définissez une hauteur d'iFrame d'au moins 500 px pour le bureau.
            </li>
            <li>
              Utilisez un CSS responsive pour que la visite s'adapte aux
              mobiles.
            </li>
            <li>
              Ajoutez un message de secours à l'intérieur des balises iFrame
              pour les navigateurs qui les bloquent.
            </li>
            <li>
              Placez la visite au-dessus de la ligne de flottaison sur les pages
              immobilières — c'est le premier contenu que voit le visiteur.
            </li>
          </ul>
        </section>

        <section>
          <h2>Questions fréquentes</h2>

          <h3>L'iFrame fonctionne-t-il avec Elementor et Divi ?</h3>
          <p>
            Oui — utilisez le widget ou module "HTML" dans ces constructeurs et
            collez le code iFrame directement. Elementor et Divi restituent les
            iFrames sans modification.
          </p>

          <h3>
            La visite virtuelle s'affiche-t-elle correctement sur mobile ?
          </h3>
          <p>
            Oui, à condition d'utiliser un wrapper responsive. Le code
            d'intégration Travvir inclut les attributs nécessaires pour une
            navigation gyroscopique sur mobile.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/integrer-visite-virtuelle-site-web">Comment Intégrer une Visite Virtuelle 360° sur son Site Web 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-seo">SEO et Visite Virtuelle : Comment Bien Référencer vos Visites en 2026</a></li>
          <li><a href="/blog/fr/guide-visite-virtuelle-smartphone">Guide Complet : Créer une Visite Virtuelle 360° avec Smartphone 2026</a></li>
          <li><a href="/blog/fr/meilleur-logiciel-visite-virtuelle-gratuit">Meilleur Logiciel de Visite Virtuelle Gratuit en 2026 (Comparatif)</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-guide-complet">Visite Virtuelle 360° : Guide Complet pour Débutants (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "visite-virtuelle-wordpress",
);

useBlogSeo({
  title:
    "Comment Ajouter une Visite Virtuelle sur WordPress : Guide Complet 2026 | Travvir",
  description:
    "Intégrez une visite virtuelle 360° sur votre site WordPress en quelques minutes : méthode iFrame, plugin, shortcode. Guide pas à pas sans codage.",
  keywords:
    "visite virtuelle wordpress, intégrer visite virtuelle wordpress, plugin visite virtuelle wordpress, iframe wordpress tour virtuel",
  slug: "visite-virtuelle-wordpress",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question: "L'iFrame fonctionne-t-il avec Elementor et Divi ?",
      answer:
        "Oui — utilisez le widget HTML dans ces constructeurs et collez le code iFrame directement.",
    },
    {
      question:
        "La visite virtuelle s'affiche-t-elle correctement sur mobile ?",
      answer:
        "Oui, avec un wrapper responsive. Le code Travvir inclut les attributs pour la navigation gyroscopique.",
    },
  ],
});

</script>
